// =============================================================================
// deliveries.ts — shared types + helpers for materials tracking (mig 382)
// =============================================================================
// James, 19 Sep 2026: "at least tracking the delivery on our end, which site
// it's for and what was delivered when / who signed it / uploaded it into Ops."
// The tables are material_deliveries (one per docket) and
// material_delivery_lines; writes go through app.save_material_delivery and
// app.mark_delivery_ticked. The docket images/PDFs live in the
// delivery-dockets bucket under deliveries/<yyyy-mm>/….
// material_deliveries is not in database.types.ts yet (regen is on the
// backlog), so the screens read it through (supabase as any) like mops does.
// =============================================================================
import { supabase, apiFetch } from "@/lib/supabase";

export const DELIVERY_BUCKET = "delivery-dockets";

export type DeliverySource = "depot_delivery" | "client_pickup" | "site_delivery";
export type DeliveryStatus = "received" | "matched" | "ticked_in_client_sheet";
export type LineStatus     = "received" | "short" | "wrong" | "damaged" | "backorder";

export const SOURCE_LABEL: Record<DeliverySource, string> = {
  depot_delivery: "Delivered to depot",
  client_pickup:  "Picked up from client warehouse",
  site_delivery:  "Delivered to site",
};

export const STATUS_LABEL: Record<DeliveryStatus, string> = {
  received:               "Received — not matched to a job",
  matched:                "Matched to job",
  ticked_in_client_sheet: "Ticked in client sheet",
};

export const LINE_STATUS_LABEL: Record<LineStatus, string> = {
  received:  "Received",
  short:     "Short",
  wrong:     "Wrong item",
  damaged:   "Damaged",
  backorder: "Back order",
};

export interface DeliveryLine {
  key:                string;          // client-side only
  part_number:        string;
  client_material_no: string;
  description:        string;
  qty_expected:       string;          // text in the form; numeric on save
  qty_received:       string;
  uom:                string;
  serials:            string;          // comma / newline separated in the form
  line_status:        LineStatus;
  note:               string;
}

export interface DeliveryHeader {
  project_id:              string | null;
  site_id:                 string | null;
  client_project_ref:      string;
  client_site_code:        string;
  client_site_name:        string;
  docket_number:           string;
  supplier:                string;
  carrier:                 string;
  source:                  DeliverySource;
  received_at:             string;     // datetime-local value
  received_by_employee_id: string | null;
  signed_by:               string;
  notes:                   string;
}

export interface DeliveryRow {
  delivery_id:             string;
  project_id:              string | null;
  site_id:                 string | null;
  client_project_ref:      string | null;
  client_site_code:        string | null;
  client_site_name:        string | null;
  docket_number:           string | null;
  supplier:                string | null;
  carrier:                 string | null;
  source:                  DeliverySource;
  received_at:             string;
  received_by_employee_id: string | null;
  signed_by:               string | null;
  document_paths:          string[];
  notes:                   string | null;
  status:                  DeliveryStatus;
  ticked_at:               string | null;
  ai_confidence:           number | null;
  created_at:              string;
  project?:  { project_code: string; project_name: string; client?: { client_id: string; client_code: string | null; legal_name: string } | null } | null;
  received_by?: { first_name: string; last_name: string } | null;
  lines?:    DeliveryLineRow[];
}

export interface DeliveryLineRow {
  line_id:            string;
  sort_order:         number;
  part_number:        string | null;
  client_material_no: string | null;
  description:        string | null;
  qty_expected:       number | null;
  qty_received:       number | null;
  uom:                string | null;
  serials:            string[];
  line_status:        LineStatus;
  note:               string | null;
}

export const DELIVERY_SELECT = `
  delivery_id, project_id, site_id, client_project_ref, client_site_code, client_site_name,
  docket_number, supplier, carrier, source, received_at, received_by_employee_id, signed_by,
  document_paths, notes, status, ticked_at, ai_confidence, created_at,
  project:project_id ( project_code, project_name, client:client_id ( client_id, client_code, legal_name ) ),
  received_by:received_by_employee_id ( first_name, last_name ),
  lines:material_delivery_lines ( line_id, sort_order, part_number, client_material_no, description,
    qty_expected, qty_received, uom, serials, line_status, note )
`;

let lineSeq = 0;
export function newLine(partial: Partial<DeliveryLine> = {}): DeliveryLine {
  lineSeq += 1;
  return {
    key: `l${Date.now()}-${lineSeq}`,
    part_number: "", client_material_no: "", description: "",
    qty_expected: "", qty_received: "", uom: "", serials: "",
    line_status: "received", note: "",
    ...partial,
  };
}

export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function emptyHeader(employeeId: string | null): DeliveryHeader {
  return {
    project_id: null, site_id: null,
    client_project_ref: "", client_site_code: "", client_site_name: "",
    docket_number: "", supplier: "", carrier: "",
    source: "depot_delivery",
    received_at: toLocalInput(new Date()),
    received_by_employee_id: employeeId,
    signed_by: "", notes: "",
  };
}

// The worker's answer for one docket. Mirrors /deliveries/ai-extract.
export interface DocketRead {
  confidence: number;
  extracted: {
    client_project_ref: string | null;
    client_site_code:   string | null;
    client_site_name:   string | null;
    state:              string | null;
    docket_number:      string | null;
    supplier:           string | null;
    carrier:            string | null;
    delivery_date:      string | null;
    signed_by:          string | null;
    handwritten_notes:  string | null;
    notes:              string | null;
    lines: Array<{
      part_number: string | null; client_material_no: string | null; description: string | null;
      qty_expected: number | null; uom: string | null; serials: string[]; line_status: LineStatus; note: string | null;
    }>;
  };
}

// Upload one docket file to the bucket. Path is deliveries/<yyyy-mm>/<ts>-<name>.
export async function uploadDocket(file: File): Promise<string> {
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const safe = file.name.replace(/[^\w.\-]/g, "_");
  const path = `deliveries/${ym}/${Date.now()}-${safe}`;
  const { error } = await supabase.storage.from(DELIVERY_BUCKET).upload(path, file, { upsert: false });
  if (error) throw new Error(`Upload failed: ${error.message}`);
  return path;
}

export async function readDocket(path: string): Promise<DocketRead> {
  const res = await apiFetch("/deliveries/ai-extract", {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify({ storage_path: path }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Couldn't read the docket (${res.status}): ${txt.slice(0, 200)}`);
  }
  return await res.json() as DocketRead;
}

export async function docketSignedUrl(path: string): Promise<string | null> {
  const { data } = await supabase.storage.from(DELIVERY_BUCKET).createSignedUrl(path, 3600);
  return data?.signedUrl ?? null;
}

export function parseSerials(s: string): string[] {
  return s.split(/[\n,;]+/).map(x => x.trim()).filter(Boolean);
}

export async function saveDelivery(
  deliveryId: string | null,
  header: DeliveryHeader,
  documentPaths: string[] | undefined,
  lines: DeliveryLine[],
  ai?: { confidence: number; raw: unknown } | null,
): Promise<string> {
  const payload: Record<string, unknown> = {
    project_id:              header.project_id,
    site_id:                 header.site_id,
    client_project_ref:      header.client_project_ref,
    client_site_code:        header.client_site_code,
    client_site_name:        header.client_site_name,
    docket_number:           header.docket_number,
    supplier:                header.supplier,
    carrier:                 header.carrier,
    source:                  header.source,
    received_at:             header.received_at ? new Date(header.received_at).toISOString() : null,
    received_by_employee_id: header.received_by_employee_id,
    signed_by:               header.signed_by,
    notes:                   header.notes,
  };
  if (documentPaths) payload.document_paths = documentPaths;   // absent = keep what's stored
  if (ai) { payload.ai_confidence = ai.confidence; payload.ai_raw = ai.raw; }

  const { data, error } = await (supabase as any).schema("app").rpc("save_material_delivery", {
    p_delivery_id: deliveryId,
    p_delivery: payload,
    p_lines: lines.map(l => ({
      part_number:        l.part_number,
      client_material_no: l.client_material_no,
      description:        l.description,
      qty_expected:       l.qty_expected === "" ? null : Number(l.qty_expected),
      qty_received:       l.qty_received === "" ? null : Number(l.qty_received),
      uom:                l.uom,
      serials:            parseSerials(l.serials),
      line_status:        l.line_status,
      note:               l.note,
    })),
  });
  if (error) throw error;
  return data as string;
}

export async function markTicked(deliveryId: string, ticked: boolean): Promise<void> {
  const { error } = await (supabase as any).schema("app").rpc("mark_delivery_ticked", { p_delivery_id: deliveryId, p_ticked: ticked });
  if (error) throw error;
}

export async function deleteDelivery(deliveryId: string): Promise<void> {
  const { error } = await (supabase as any).schema("app").rpc("delete_material_delivery", { p_delivery_id: deliveryId });
  if (error) throw error;
}

export function rowToForm(r: DeliveryRow): { header: DeliveryHeader; lines: DeliveryLine[] } {
  return {
    header: {
      project_id: r.project_id, site_id: r.site_id,
      client_project_ref: r.client_project_ref ?? "", client_site_code: r.client_site_code ?? "",
      client_site_name: r.client_site_name ?? "", docket_number: r.docket_number ?? "",
      supplier: r.supplier ?? "", carrier: r.carrier ?? "", source: r.source,
      received_at: toLocalInput(new Date(r.received_at)),
      received_by_employee_id: r.received_by_employee_id, signed_by: r.signed_by ?? "", notes: r.notes ?? "",
    },
    lines: (r.lines ?? []).slice().sort((a, b) => a.sort_order - b.sort_order).map(l => newLine({
      part_number: l.part_number ?? "", client_material_no: l.client_material_no ?? "",
      description: l.description ?? "",
      qty_expected: l.qty_expected == null ? "" : String(l.qty_expected),
      qty_received: l.qty_received == null ? "" : String(l.qty_received),
      uom: l.uom ?? "", serials: (l.serials ?? []).join(", "),
      line_status: l.line_status, note: l.note ?? "",
    })),
  };
}
